import axiosInstance from './axiosInstance'

const COUNTRY_BASE = '/countries'

let countriesCache = null
let cacheTimestamp = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutos

/**
 * Invalida el cache de países
 */
export const invalidateCountriesCache = () => {
  countriesCache = null
  cacheTimestamp = null
}

/**
 * Obtiene todos los países (para administración)
 * @returns {Promise<Array>} Lista de países
 */
export const fetchAllCountries = async (options = {}) => {
  const { force = false, noCache = false } = options
  const bypassCache = force || noCache
  const now = Date.now()

  if (!bypassCache && countriesCache && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
    return countriesCache
  }

  try {
    const requestConfig = {}
    if (noCache) {
      requestConfig.headers = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      }
      requestConfig.params = { _ts: now }
    }

    const response = await axiosInstance.get(COUNTRY_BASE, requestConfig)
    const rawData = response.data.countries || response.data.res || []
    const mapped = rawData.map(c => ({
      id: c.id || c.id_pais,
      nombre: c.nombre,
      slug: c.slug,
      codigo_iso2: c.codigo_iso2,
      codigo_iso3: c.codigo_iso3,
      label_n1: c.label_n1,
      label_n2: c.label_n2,
      label_n3: c.label_n3,
      habilitado: c.habilitado !== undefined ? Boolean(c.habilitado) : Boolean(c.habilitado?.data?.[0]),
      orden: c.orden,
      icono_svg: c.icono_svg || null,
      center: c.center || (c.center_lng != null && c.center_lat != null
        ? [parseFloat(c.center_lng), parseFloat(c.center_lat)]
        : null),
      zoom: c.zoom ?? 6,
    }))

    countriesCache = mapped
    cacheTimestamp = now
    return mapped
  } catch (error) {
    console.error('Error fetching countries:', error)
    return countriesCache || []
  }
}

/**
 * Crea un nuevo país
 * @param {Object} data - Datos del país
 */
export const createCountry = async data => {
  try {
    const response = await axiosInstance.post(COUNTRY_BASE, data)
    invalidateCountriesCache()
    return { success: true, country: response.data.data }
  } catch (error) {
    console.error('Error creating country:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al crear el país',
    }
  }
}

/**
 * Actualiza un país existente
 * @param {number} id - ID del país
 * @param {Object} data - Datos a actualizar
 */
export const updateCountry = async (id, data) => {
  try {
    const response = await axiosInstance.put(`${COUNTRY_BASE}/${id}`, data)
    invalidateCountriesCache()
    return { success: true, country: response.data.data }
  } catch (error) {
    console.error('Error updating country:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al actualizar el país',
    }
  }
}

/**
 * Elimina un país
 * @param {number} id - ID del país
 */
export const deleteCountry = async id => {
  try {
    await axiosInstance.delete(`${COUNTRY_BASE}/${id}`)
    invalidateCountriesCache()
    return { success: true }
  } catch (error) {
    console.error('Error deleting country:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al eliminar el país',
    }
  }
}

/**
 * Habilita un país
 * @param {number} id - ID del país
 */
export const enableCountry = async id => {
  try {
    const response = await axiosInstance.patch(`${COUNTRY_BASE}/${id}/enable`)
    invalidateCountriesCache()
    return { success: true, country: response.data.data }
  } catch (error) {
    console.error('Error enabling country:', error)
    return { success: false, message: 'Error al habilitar el país' }
  }
}

/**
 * Deshabilita un país
 * @param {number} id - ID del país
 */
export const disableCountry = async id => {
  try {
    const response = await axiosInstance.patch(`${COUNTRY_BASE}/${id}/disable`)
    invalidateCountriesCache()
    return { success: true, country: response.data.data }
  } catch (error) {
    console.error('Error disabling country:', error)
    return { success: false, message: 'Error al deshabilitar el país' }
  }
}
